"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import type { Id } from "@agroproj/backend/convex/_generated/dataModel";
import { getAssistantRecordPath } from "@agroproj/backend/convex/assistant/routeContext";
import { FEATURE_FLAG_KEYS } from "@agroproj/backend/convex/constants/featureFlags";
import type { CusteioDraft } from "@agroproj/backend/convex/documentDrafts/assemble";
import { useConvex, useMutation, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import type { Route } from "next";
import Link from "next/link";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Section } from "@/components/ui/section";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useConvexFeatureFlag } from "@/hooks/use-convex-feature-flag";
import { acceptanceFromChoices, deriveDraftPhase, DRAFT_PHASE_LABELS, EMPTY_CHOICES, FIELD_STATE_LABELS, shouldWarnUnsavedDraft, type DraftChoices, type SectionChoice } from "@/lib/custeio-document-draft";
import { formatCurrencyBRL } from "@/lib/format";
type Fingerprint = {
  documentId: Id<"project_documents">;
  contentVersion: number;
  contentSha256: string;
};
function errorMessage(cause: unknown) {
  if (cause instanceof ConvexError && typeof cause.data === "string") return cause.data;
  if (cause instanceof Error && cause.message.trim()) return cause.message;
  return "Não foi possível preparar o rascunho.";
}
function producerOption(row: {
  [key: string]: unknown;
}) {
  if (typeof row._id !== "string") return null;
  const name = typeof row.name === "string" ? row.name : "";
  const company = typeof row.companyName === "string" ? row.companyName : "";
  const trading = typeof row.tradingName === "string" ? row.tradingName : "";
  return {
    value: row._id as Id<"producers">,
    label: name || company || trading || "Produtor"
  };
}
export function CusteioDocumentDraft(t0) {
  const $ = _c(2);
  const {
    organizationSlug
  } = t0;
  const flag = useConvexFeatureFlag(FEATURE_FLAG_KEYS.PROJECT_DOCUMENT_DRAFT);
  if (!flag.enabled) {
    return null;
  }
  let t1;
  if ($[0] !== organizationSlug) {
    t1 = <DraftPanel organizationSlug={organizationSlug} />;
    $[0] = organizationSlug;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function DraftPanel({
  organizationSlug
}: {
  organizationSlug: string;
}) {
  const convex = useConvex();
  const producers = useQuery(api.producers.queries.getProducers, {
    limit: 50,
    status: "active"
  });
  const prepareProposal = useMutation(api.assistant.projects.prepare);
  const confirmDraft = useMutation(api.assistant.projects.confirmCusteioDraft);
  const consentId = useId();
  const saveLock = useRef(false);
  const requestKey = useRef<string | null>(null);
  const requestPayload = useRef("");
  const [producerId, setProducerId] = useState<Id<"producers"> | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [prompt, setPrompt] = useState("");
  const [consent, setConsent] = useState(false);
  const [draft, setDraft] = useState<CusteioDraft | null>(null);
  const [prepared, setPrepared] = useState<{
    prompt: string;
    fingerprints: Fingerprint[];
    key: string;
    reviewFingerprint: string;
  } | null>(null);
  const [choices, setChoices] = useState<DraftChoices>(EMPTY_CHOICES);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [discarded, setDiscarded] = useState(false);
  const [savedId, setSavedId] = useState<Id<"projects"> | null>(null);
  const documents = useQuery(api.projectDocuments.queries.listProducerDraftDocuments, producerId ? {
    producerId
  } : "skip");
  const options = (producers?.producers ?? []).flatMap(row => {
    const option = producerOption(row);
    return option ? [option] : [];
  });
  const fingerprints = useMemo<Fingerprint[]>(() => (documents ?? []).flatMap(document => selected[document.documentId] && document.extractionReady ? [{
    documentId: document.documentId,
    contentVersion: document.contentVersion,
    contentSha256: document.contentSha256
  }] : []), [documents, selected]);
  const fingerprintKey = fingerprints.map(item => `${item.documentId}:${item.contentVersion}:${item.contentSha256}`).join("|");
  const stale = prepared !== null && (prepared.prompt !== prompt.trim() || prepared.key !== fingerprintKey);
  const phase = deriveDraftPhase({
    pending,
    error: error.length > 0,
    discarded,
    stale,
    saved: savedId !== null,
    ready: draft !== null,
    choices
  });
  const warn = shouldWarnUnsavedDraft({
    saved: savedId !== null,
    choices
  });
  useEffect(() => {
    if (!warn) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    const onClick = (event_0: MouseEvent) => {
      const target = event_0.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a");
      if (!anchor) return;
      if (!window.confirm("As seções aceitas ainda não foram salvas. Descartar?")) {
        event_0.preventDefault();
        event_0.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("click", onClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onClick, true);
    };
  }, [warn]);
  function resetReview() {
    setDraft(null);
    setPrepared(null);
    setChoices(EMPTY_CHOICES);
    setSavedId(null);
    setError("");
  }
  async function prepare() {
    if (pending || !producerId || !consent || fingerprints.length < 1 || !prompt.trim()) return;
    setPending(true);
    setError("");
    setDiscarded(false);
    try {
      const next = await convex.query(api.assistant.projects.previewCusteioDraft, {
        producerId,
        prompt: prompt.trim(),
        fingerprints,
        accepted: {
          basic: false,
          report: false,
          budget: false
        }
      });
      setDraft(next.draft);
      setPrepared({
        prompt: prompt.trim(),
        fingerprints,
        key: fingerprintKey,
        reviewFingerprint: next.reviewFingerprint
      });
      setChoices(EMPTY_CHOICES);
      setSavedId(null);
    } catch (cause) {
      setDraft(null);
      setPrepared(null);
      setError(errorMessage(cause));
    } finally {
      setPending(false);
    }
  }
  function choose(section: keyof DraftChoices, choice: SectionChoice) {
    setChoices(current => ({
      ...current,
      [section]: choice
    }));
    setSavedId(null);
    setDiscarded(false);
  }
  async function save() {
    if (saveLock.current || pending || stale || !draft || !prepared || !producerId || choices.basic !== "accepted") {
      return;
    }
    saveLock.current = true;
    setPending(true);
    setError("");
    try {
      const accepted = acceptanceFromChoices(choices);
      const payload = JSON.stringify({
        producerId,
        prompt: prepared.prompt,
        fingerprints: prepared.fingerprints,
        accepted
      });
      let key = requestKey.current;
      if (requestPayload.current !== payload || !key) {
        requestPayload.current = payload;
        key = crypto.randomUUID();
        requestKey.current = key;
      }
      const proposal = await prepareProposal({
        organizationSlug,
        requestKey: key,
        action: {
          type: "document_draft",
          producerId,
          draft: {
            reviewFingerprint: prepared.reviewFingerprint,
            prompt: prepared.prompt,
            fingerprints: prepared.fingerprints,
            accepted
          }
        }
      });
      const projectId = await confirmDraft({
        proposalId: proposal.proposalId,
        confirmed: true
      });
      setSavedId(projectId);
    } catch (cause_0) {
      setError(errorMessage(cause_0));
    } finally {
      saveLock.current = false;
      setPending(false);
    }
  }
  const titles = new Map<string, string>((documents ?? []).map(document_0 => [document_0.documentId, document_0.title]));
  return <Section title="Rascunho de custeio" actions={phase ? <span>{DRAFT_PHASE_LABELS[phase]}</span> : null}>
      <p>Aceitar uma seção não grava. Salvar custeio grava somente as seções aceitas.</p>
      {options.length > 0 ? <Select items={options} label="Produtor" value={producerId} onValueChange={value => {
      const match = options.find(option_0 => option_0.value === value);
      setProducerId(match?.value ?? null);
      setSelected({});
      resetReview();
    }}>
          <SelectTrigger>
            <SelectValue placeholder="Selecione" />
          </SelectTrigger>
          <SelectContent>
            {options.map(option_1 => <SelectItem key={option_1.value} value={option_1.value}>
                {option_1.label}
              </SelectItem>)}
          </SelectContent>
        </Select> : <p>Nenhum produtor ativo.</p>}
      <p>
        Antes de selecionar, prepare a extração na conferência de documentos do projeto. Volte aqui
        depois de conferir.
      </p>
      <ul className="flex flex-col gap-2">
        {(documents ?? []).map(document_1 => <li key={document_1.documentId} className="flex items-center gap-2">
            <Checkbox aria-label={document_1.title} disabled={pending || !document_1.extractionReady} checked={selected[document_1.documentId] === true} onCheckedChange={checked => {
          setSelected(current_0 => {
            const next_0 = {
              ...current_0,
              [document_1.documentId]: checked
            };
            const count = Object.values(next_0).filter(Boolean).length;
            return checked && count > 3 ? current_0 : next_0;
          });
        }} />
            <span>{document_1.title}</span>
            {!document_1.extractionReady ? <Link href={`/${organizationSlug}/projects/${document_1.projectId}?tab=documents` as Route}>
                Preparar no projeto
              </Link> : null}
          </li>)}
      </ul>
      {producerId && documents?.length === 0 ? <p>Nenhum documento pronto.</p> : null}
      <Textarea label="Pedido" maxLength={500} value={prompt} onChange={event_1 => setPrompt(event_1.target.value)} />
      <div className="flex items-center gap-2">
        <Checkbox id={consentId} checked={consent} onCheckedChange={checked_0 => setConsent(checked_0)} />
        <label htmlFor={consentId}>Autorizo o uso do texto selecionado neste rascunho.</label>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" type="button" variant="outline" disabled={pending} onClick={() => void prepare()}>
          Preparar
        </Button>
        <Button size="sm" type="button" variant="ghost" onClick={() => {
        resetReview();
        setDiscarded(true);
      }}>
          Descartar
        </Button>
        <Button size="sm" type="button" disabled={pending || stale || !draft || choices.basic !== "accepted" || savedId !== null} onClick={() => void save()}>
          Salvar custeio
        </Button>
      </div>
      {stale ? <p>Os documentos mudaram. Prepare de novo.</p> : null}
      {error ? <p>{error}</p> : null}
      {draft ? <div className="flex flex-col gap-3">
          <ul className="flex flex-col gap-1">
            {draft.fields.map(field => <li key={field.key}>
                {field.label}: {field.value ?? "sem valor"} ({FIELD_STATE_LABELS[field.state]}
                {field.state === "source" && field.line ? `, ${titles.get(field.documentId ?? "") ?? "documento"}, linha ${field.line}` : ""}
                )
              </li>)}
          </ul>
          <p>Ausente: {draft.missing.join(", ")}</p>
          <SectionChoiceRow title="Dados básicos" choice={choices.basic} onChoose={choice_0 => choose("basic", choice_0)} />
          <div>
            <p className="whitespace-pre-wrap">
              {draft.report.state === "source" ? draft.report.text : "Relato sem fonte."}
            </p>
            <SectionChoiceRow title="Relato técnico" choice={choices.report} onChoose={choice_1 => choose("report", choice_1)} />
          </div>
          <div>
            {draft.budget.state === "source" ? <ul>
                {draft.budget.lines.map(line => <li key={`${line.supplyId}:${line.line}`}>
                    {line.title}: {formatCurrencyBRL(line.totalValue)} no total,{" "}
                    {formatCurrencyBRL(line.ownResourcesValue)} de recursos próprios,{" "}
                    {line.expectedAcquisitionType === "immediate" ? "imediata" : "planejada"}
                  </li>)}
              </ul> : <p>Orçamento incompleto.</p>}
            <SectionChoiceRow title="Orçamento" choice={choices.budget} onChoose={choice_2 => choose("budget", choice_2)} />
          </div>
        </div> : null}
      {savedId ? <Link className="underline underline-offset-4" href={getAssistantRecordPath({
      organizationSlug,
      recordType: "project",
      recordId: savedId
    }) as Route}>
          Abrir projeto
        </Link> : null}
    </Section>;
}
function SectionChoiceRow(t0) {
  const $ = _c(13);
  const {
    title,
    choice,
    onChoose
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <span>{title}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = choice === "accepted" ? "secondary" : "outline";
  let t3;
  if ($[2] !== onChoose) {
    t3 = () => onChoose("accepted");
    $[2] = onChoose;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <Button size="sm" type="button" variant={t2} onClick={t3}>Aceitar</Button>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onChoose) {
    t5 = <Button size="sm" type="button" variant="ghost" onClick={() => onChoose("rejected")}>Recusar</Button>;
    $[7] = onChoose;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t4 || $[11] !== t5) {
    t6 = <div className="flex flex-wrap items-center gap-2">{t1}{t4}{t5}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}